import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Shield, CheckCircle, AlertCircle, Loader2, ExternalLink, Clock, Zap, Info, Download } from 'lucide-react';
import { useWallet } from '@txnlab/use-wallet-react';
import { formatShortAddress } from '@txnlab/utils-ts';
import { WalletButton } from '../wallet/WalletButton';
import { useNotarization, verifyNotarization, getAlgorandExplorerUrl, formatTransactionId } from '../../lib/notarization';
import { useDocuments } from '../../hooks/useDocuments';
import Button from '../atoms/Button';
import Card from '../atoms/Card';
import toast from 'react-hot-toast';

interface NotarizationButtonProps {
  documentId: string;
  fileName: string;
  isNotarized: boolean;
  notarizationHash?: string;
  onNotarizationComplete?: (transactionId: string) => void;
  className?: string;
}

type NotarizationStatus = 'idle' | 'connecting' | 'hashing' | 'submitting' | 'confirming' | 'updating' | 'completed' | 'error';

const NotarizationButton: React.FC<NotarizationButtonProps> = ({
  documentId,
  fileName,
  isNotarized,
  notarizationHash,
  onNotarizationComplete,
  className = ''
}) => {
  const [status, setStatus] = useState<NotarizationStatus>('idle');
  const [showDetails, setShowDetails] = useState(false);
  const [transactionId, setTransactionId] = useState<string | null>(null);
  const [documentHash, setDocumentHash] = useState<string | null>(null);
  const [confirmationTime, setConfirmationTime] = useState<string | null>(null);
  const [isCheckingWallet, setIsCheckingWallet] = useState(true);
  
  const { activeAddress } = useWallet();
  const { notarizeWithConnectedWallet } = useNotarization();
  const { updateDocument, refreshDocument } = useDocuments();

  // Auto-refresh document status when notarization is in progress
  useEffect(() => {
    let interval: NodeJS.Timeout;
    
    if (status === 'confirming' && transactionId) {
      interval = setInterval(async () => {
        try {
          const verification = await verifyNotarization(documentId);
          if (verification.isNotarized && verification.transactionId) {
            setStatus('completed');
            setConfirmationTime(new Date().toISOString());
            toast.success('Blockchain confirmation received!', { 
              icon: '🎉',
              duration: 5000 
            });
            clearInterval(interval);
            
            // Refresh the document data
            await refreshDocument(documentId);
          }
        } catch (error) {
          console.error('Verification check failed:', error);
        }
      }, 3000); // Check every 3 seconds
    }

    return () => {
      if (interval) clearInterval(interval);
    };
  }, [status, transactionId, documentId, refreshDocument]);

  const getStatusMessage = () => {
    switch (status) {
      case 'connecting':
        return 'Connecting to Algorand wallet...';
      case 'hashing':
        return 'Calculating document hash...';
      case 'submitting':
        return 'Submitting to blockchain...';
      case 'confirming':
        return 'Waiting for blockchain confirmation...';
      case 'updating':
        return 'Updating document status...';
      case 'completed':
        return 'Notarization completed successfully!';
      case 'error':
        return 'Notarization failed';
      default:
        return '';
    }
  };

  const getStatusIcon = () => {
    switch (status) {
      case 'connecting':
      case 'hashing':
      case 'submitting':
      case 'updating':
        return <Loader2 className="h-4 w-4 animate-spin" />;
      case 'confirming':
        return <Clock className="h-4 w-4 text-blue-500" />;
      case 'completed':
        return <CheckCircle className="h-4 w-4 text-green-500" />;
      case 'error':
        return <AlertCircle className="h-4 w-4 text-red-500" />;
      default:
        return <Shield className="h-4 w-4" />;
    }
  };

  const handleNotarization = async () => {
    // Check if wallet is connected
    if (!activeAddress) {
      toast.error('Please connect your Algorand wallet first');
      return;
    }

    try {
      setStatus('connecting');
      toast.loading('Starting blockchain notarization...', { id: 'notarization' });
      
      // Step 1: Create mock file for notarization (in production, this would be the actual file)
      setStatus('hashing');
      toast.loading('Calculating document hash...', { id: 'notarization' });
      
      const mockFileContent = `Document: ${fileName}\nID: ${documentId}\nTimestamp: ${new Date().toISOString()}`;
      const mockFile = new File([mockFileContent], fileName, { type: 'text/plain' });
      
      setStatus('submitting');
      toast.loading('Submitting to Algorand blockchain...', { id: 'notarization' });
      
      // Step 2: Perform notarization using the connected wallet
      const result = await notarizeWithConnectedWallet(mockFile, documentId);
      
      if (!result.success) {
        throw new Error(result.error || 'Notarization failed');
      }
      
      // Store transaction details
      setTransactionId(result.transactionId!);
      setDocumentHash(result.documentHash!);
      
      setStatus('confirming');
      toast.loading('Waiting for blockchain confirmation...', { 
        id: 'notarization',
        duration: 10000 
      });
      
      // Step 3: Wait for confirmation
      await new Promise(resolve => setTimeout(resolve, 3000));
      
      setStatus('updating');
      toast.loading('Updating document status...', { id: 'notarization' });
      
      // Step 4: Update document in database
      await updateDocument(documentId, {
        isNotarized: true,
        notarizationHash: result.transactionId,
        lastModified: new Date()
      });
      
      setStatus('completed');
      setConfirmationTime(new Date().toISOString());
      
      toast.success('Document successfully notarized on Algorand blockchain!', { 
        id: 'notarization',
        icon: '🎉',
        duration: 6000 
      });
      
      // Call completion callback
      if (onNotarizationComplete && result.transactionId) {
        onNotarizationComplete(result.transactionId);
      }
      
    } catch (error: any) {
      console.error('Notarization error:', error);
      setStatus('error');
      toast.error(error.message || 'Notarization failed', { 
        id: 'notarization',
        duration: 5000 
      });
      
      // Reset status after error
      setTimeout(() => setStatus('idle'), 3000);
    }
  };

  const getAlgorandExplorerUrl = (txId: string) => {
    return `https://algoexplorer.io/tx/${txId}`;
  };

  const formatTransactionId = (txId: string) => {
    if (txId.length <= 16) return txId;
    return `${txId.slice(0, 8)}...${txId.slice(-8)}`;
  };

  const isRealTransaction = (txId: string) => {
    return !txId.startsWith('NOTARIZE_');
  };

  // If already notarized, show confirmation UI
  if ((isNotarized && notarizationHash) || status === 'completed') {
    const displayTxId = transactionId || notarizationHash;
    const displayTime = confirmationTime || new Date().toISOString();
    const isReal = displayTxId ? isRealTransaction(displayTxId) : false;
    
    return (
      <div className={`space-y-3 ${className}`}>
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="flex items-center gap-2 p-3 rounded-lg border bg-green-50 border-green-200"
        >
          <CheckCircle className="h-5 w-5 text-green-500 flex-shrink-0" />
          <div className="flex-1">
            <p className="text-sm font-medium text-green-800">
              Blockchain Notarized
            </p>
            <p className="text-xs text-green-600">
              Permanently recorded on Algorand
            </p>
          </div>
          <Zap className="h-4 w-4 text-green-500" />
        </motion.div>
        
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowDetails(!showDetails)}
            className="flex-1"
          >
            {showDetails ? 'Hide Details' : 'View Details'}
          </Button>
          
          {displayTxId && isReal && (
            <Button
              variant="ghost"
              size="sm"
              icon={ExternalLink}
              onClick={() => window.open(getAlgorandExplorerUrl(displayTxId), '_blank')}
              className="text-blue-600 hover:text-blue-700"
            >
              Explorer
            </Button>
          )}
        </div>
        
        <AnimatePresence>
          {showDetails && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.3 }}
            >
              <Card className="p-4 space-y-3 bg-green-50 border-green-200">
                <div className="grid grid-cols-1 gap-3">
                  <div>
                    <p className="text-xs font-medium mb-1 text-green-800">
                      Transaction ID:
                    </p>
                    <div className="flex items-center gap-2">
                      <p className="text-xs font-mono break-all flex-1 text-green-700">
                        {displayTxId}
                      </p>
                      {isReal && (
                        <Button
                          variant="ghost"
                          size="sm"
                          icon={ExternalLink}
                          onClick={() => displayTxId && window.open(getAlgorandExplorerUrl(displayTxId), '_blank')}
                          className="p-1 h-6 w-6"
                        />
                      )}
                    </div>
                  </div>
                  
                  {documentHash && (
                    <div>
                      <p className="text-xs font-medium mb-1 text-green-800">Document Hash:</p>
                      <p className="text-xs font-mono break-all text-green-700">
                        {documentHash}
                      </p>
                    </div>
                  )}
                  
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <p className="text-xs font-medium mb-1 text-green-800">Blockchain:</p>
                      <p className="text-xs text-green-700">Algorand Mainnet</p>
                    </div>
                    <div>
                      <p className="text-xs font-medium mb-1 text-green-800">Status:</p>
                      <p className="text-xs flex items-center gap-1 text-green-700">
                        <CheckCircle className="h-3 w-3" />
                        Confirmed
                      </p>
                    </div>
                  </div>
                  
                  <div>
                    <p className="text-xs font-medium mb-1 text-green-800">Confirmed At:</p>
                    <p className="text-xs text-green-700">
                      {new Date(displayTime).toLocaleString()}
                    </p>
                  </div>
                </div>
              </Card>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    );
  }

  // Show notarization process UI
  return (
    <div className={`space-y-3 ${className}`}>
      {!activeAddress ? (
        // No wallet connected
        <div className="space-y-3">
          <div className="p-3 rounded-lg border bg-blue-50 border-blue-200">
            <div className="flex items-start gap-2">
              <Info className="h-4 w-4 text-blue-600 flex-shrink-0 mt-0.5" />
              <div className="text-xs text-blue-700">
                <p className="font-medium mb-1">Algorand Wallet Required</p>
                <p>Connect your wallet to notarize this document on the blockchain.</p>
              </div>
            </div>
          </div>
          
          <WalletButton 
            variant="primary"
            size="sm"
            className="w-full"
          />
          
          <div className="text-xs text-gray-500 space-y-1">
            <p className="flex items-center gap-1">
              <Shield className="h-3 w-3" />
              Secure blockchain verification
            </p>
            <p className="flex items-center gap-1">
              <CheckCircle className="h-3 w-3" />
              Immutable proof of authenticity
            </p>
          </div>
        </div>
      ) : (
        // Wallet connected, show notarization button
        <>
          <Button
            variant="primary"
            size="sm"
            icon={status === 'idle' ? Shield : undefined}
            onClick={handleNotarization}
            disabled={status !== 'idle'}
            className="w-full"
          >
            {status === 'idle' ? 'Notarize on Blockchain' : 'Notarizing...'}
          </Button>
          
          {/* Process Status */}
          <AnimatePresence>
            {status !== 'idle' && status !== 'completed' && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ duration: 0.3 }}
              >
                <Card className={`p-3 ${
                  status === 'error' 
                    ? 'bg-red-50 border-red-200' 
                    : 'bg-blue-50 border-blue-200'
                }`}>
                  <div className="flex items-center gap-2">
                    {getStatusIcon()}
                    <div className={`text-xs ${
                      status === 'error' ? 'text-red-700' : 'text-blue-700'
                    }`}>
                      <p className="font-medium">
                        {status === 'error' ? 'Notarization Failed' : 'Blockchain Notarization'}
                      </p>
                      <p>{getStatusMessage()}</p>
                    </div>
                  </div>
                  
                  {/* Progress indicator */}
                  {status !== 'error' && (
                    <div className="mt-2">
                      <div className="w-full bg-blue-200 rounded-full h-1">
                        <motion.div
                          className="bg-blue-600 h-1 rounded-full"
                          initial={{ width: '0%' }}
                          animate={{ 
                            width: status === 'connecting' ? '20%' :
                                  status === 'hashing' ? '40%' :
                                  status === 'submitting' ? '60%' :
                                  status === 'confirming' ? '80%' :
                                  status === 'updating' ? '95%' : '100%'
                          }}
                          transition={{ duration: 0.5 }}
                        />
                      </div>
                    </div>
                  )}
                </Card>
              </motion.div>
            )}
          </AnimatePresence>
          
          {/* Wallet Info */}
          {status === 'idle' && (
            <div className="text-xs text-gray-500 space-y-1">
              <p className="flex items-center gap-1">
                <Shield className="h-3 w-3" />
                Algorand blockchain verification
              </p>
              <p className="flex items-center gap-1">
                <CheckCircle className="h-3 w-3" />
                Immutable proof of authenticity
              </p>
              <p className="flex items-center gap-1">
                <Zap className="h-3 w-3 text-green-500" />
                Connected as {formatShortAddress(activeAddress)}
              </p>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default NotarizationButton;